import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { trainingApi, usersApi } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
    GraduationCap,
    RefreshCw,
    Plus,
    Calendar,
    Award,
    AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, cn } from '../lib/utils';

const trainingTypes = [
    'Food Safety Level 2',
    'Food Safety Level 3',
    'Allergen Awareness',
    'HACCP Training',
    'Fire Safety',
    'First Aid',
    'Manual Handling',
    'Health & Safety Induction',
    'SFBB Training',
    'Other'
];

const Training = () => {
    const { user, isManagerOrAdmin: isManager } = useAuth();
    const [records, setRecords] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    
    // Form state
    const [selectedUser, setSelectedUser] = useState('');
    const [trainingType, setTrainingType] = useState('');
    const [description, setDescription] = useState('');
    const [completedDate, setCompletedDate] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [certificateNumber, setCertificateNumber] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [trainingRes] = await Promise.all([
                trainingApi.getAll()
            ]);
            setRecords(trainingRes.data);
            
            if (isManager) {
                const usersRes = await usersApi.getAll();
                setUsers(usersRes.data);
            }
        } catch (error) {
            console.error('Failed to load training data:', error);
            toast.error('Failed to load training data');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setSelectedUser('');
        setTrainingType('');
        setDescription('');
        setCompletedDate('');
        setExpiryDate('');
        setCertificateNumber('');
    };

    const handleSubmit = async () => {
        if (!selectedUser || !trainingType || !completedDate) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            const response = await trainingApi.create({
                user_id: selectedUser,
                training_type: trainingType,
                description: description || trainingType,
                completed_date: completedDate,
                expiry_date: expiryDate || null,
                certificate_number: certificateNumber || null
            });
            
            setRecords([response.data, ...records]);
            setDialogOpen(false);
            resetForm();
            toast.success('Training record added successfully');
        } catch (error) {
            console.error('Failed to add training record:', error);
            toast.error('Failed to add training record');
        }
    };

    const isExpired = (expiryDate) => {
        if (!expiryDate) return false;
        return new Date(expiryDate) < new Date();
    };

    const isExpiringSoon = (expiryDate) => {
        if (!expiryDate) return false;
        const expiry = new Date(expiryDate);
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
        return expiry <= thirtyDaysFromNow && expiry > new Date();
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-8 w-8 brand-icon animate-spin" />
                </div>
            </Layout>
        );
    }

    // Group records by user
    const groupedRecords = records.reduce((acc, record) => {
        const key = record.user_name;
        if (!acc[key]) acc[key] = [];
        acc[key].push(record);
        return acc;
    }, {});

    return (
        <Layout>
            <div className="space-y-6" data-testid="training-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-100">Staff Training</h1>
                        <p className="text-slate-400 mt-1">
                            Track and manage staff training records
                        </p>
                    </div>
                    {isManager && (
                        <Dialog open={dialogOpen} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) resetForm();
                        }}>
                            <DialogTrigger asChild>
                                <Button className="brand-btn" data-testid="add-training-button">
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Training
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-slate-800">
                                <DialogHeader>
                                    <DialogTitle className="text-slate-100">Add Training Record</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 pt-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Staff Member *</Label>
                                        <Select value={selectedUser} onValueChange={setSelectedUser}>
                                            <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200" data-testid="training-user-select">
                                                <SelectValue placeholder="Select staff member" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {users.map(u => (
                                                    <SelectItem key={u.id} value={u.id} className="text-slate-200 focus:bg-slate-800">
                                                        {u.name} ({u.role})
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Training Type *</Label>
                                        <Select value={trainingType} onValueChange={setTrainingType}>
                                            <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200" data-testid="training-type-select">
                                                <SelectValue placeholder="Select training type" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {trainingTypes.map(type => (
                                                    <SelectItem key={type} value={type} className="text-slate-200 focus:bg-slate-800">
                                                        {type}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Description</Label>
                                        <Textarea
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="Additional details..."
                                            className="bg-slate-950 border-slate-800 text-slate-200"
                                            data-testid="training-description-input"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300">Completed Date *</Label>
                                            <Input
                                                type="date"
                                                value={completedDate}
                                                onChange={(e) => setCompletedDate(e.target.value)}
                                                className="bg-slate-950 border-slate-800 text-slate-200"
                                                data-testid="training-completed-date"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300">Expiry Date</Label>
                                            <Input
                                                type="date"
                                                value={expiryDate}
                                                onChange={(e) => setExpiryDate(e.target.value)}
                                                className="bg-slate-950 border-slate-800 text-slate-200"
                                                data-testid="training-expiry-date"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Certificate Number</Label>
                                        <Input
                                            value={certificateNumber}
                                            onChange={(e) => setCertificateNumber(e.target.value)}
                                            placeholder="e.g., CERT-12345"
                                            className="bg-slate-950 border-slate-800 text-slate-200"
                                            data-testid="training-certificate-input"
                                        />
                                    </div>
                                    <Button
                                        onClick={handleSubmit}
                                        className="w-full brand-btn"
                                        data-testid="training-submit-button"
                                    >
                                        Add Training Record
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>

                {/* Training Records by User */}
                {Object.keys(groupedRecords).length > 0 ? (
                    <div className="space-y-6">
                        {Object.entries(groupedRecords).map(([userName, userRecords]) => (
                            <Card key={userName} className="bg-slate-900 border-slate-800">
                                <CardHeader className="border-b border-slate-800">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-full brand-bg-subtle flex items-center justify-center">
                                            <span className="brand-text font-semibold">
                                                {userName.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <CardTitle className="text-lg text-slate-100">{userName}</CardTitle>
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-4">
                                    <div className="space-y-3">
                                        {userRecords.map(record => (
                                            <div
                                                key={record.id}
                                                className={cn(
                                                    'flex items-center justify-between p-4 rounded-lg border',
                                                    isExpired(record.expiry_date)
                                                        ? 'bg-rose-500/5 border-rose-500/20'
                                                        : isExpiringSoon(record.expiry_date)
                                                            ? 'bg-amber-500/5 border-amber-500/20'
                                                            : 'bg-slate-800/50 border-slate-800'
                                                )}
                                                data-testid={`training-record-${record.id}`}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className={cn(
                                                        'h-10 w-10 rounded-lg flex items-center justify-center',
                                                        isExpired(record.expiry_date)
                                                            ? 'bg-rose-500/10'
                                                            : isExpiringSoon(record.expiry_date)
                                                                ? 'bg-amber-500/10'
                                                                : 'bg-emerald-500/10'
                                                    )}>
                                                        {isExpired(record.expiry_date) ? (
                                                            <AlertTriangle className="h-5 w-5 text-rose-400" />
                                                        ) : (
                                                            <GraduationCap className={cn(
                                                                'h-5 w-5',
                                                                isExpiringSoon(record.expiry_date) ? 'text-amber-400' : 'text-emerald-400'
                                                            )} />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-slate-200">{record.training_type}</p>
                                                        <div className="flex items-center gap-3 text-sm text-slate-400">
                                                            <span className="flex items-center gap-1">
                                                                <Calendar className="h-3 w-3" />
                                                                {formatDate(record.completed_date)}
                                                            </span>
                                                            {record.certificate_number && (
                                                                <span className="flex items-center gap-1">
                                                                    <Award className="h-3 w-3" />
                                                                    {record.certificate_number}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    {record.expiry_date ? (
                                                        <Badge className={cn(
                                                            isExpired(record.expiry_date)
                                                                ? 'bg-rose-500/10 text-rose-400 border-rose-500/20'
                                                                : isExpiringSoon(record.expiry_date)
                                                                    ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                                                    : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                                        )}>
                                                            {isExpired(record.expiry_date)
                                                                ? 'Expired'
                                                                : isExpiringSoon(record.expiry_date)
                                                                    ? 'Expiring Soon'
                                                                    : `Expires ${formatDate(record.expiry_date)}`}
                                                        </Badge>
                                                    ) : (
                                                        <Badge className="bg-slate-700 text-slate-400">
                                                            No Expiry
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="py-12">
                            <div className="empty-state">
                                <GraduationCap className="empty-state-icon" />
                                <p className="empty-state-title">No training records</p>
                                <p className="empty-state-description">
                                    {isManager ? 'Add your first training record to track staff certifications.' : 'No training records found for your account.'}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
};

export default Training;
